// İSG uzmanı portalı — Taşeron/Yüklenici detayı (api-contract §13). S33 Slice 6 —
// `PermitDetailPage.tsx`'in anatomisini AYNALAR: Breadcrumb → PageHeader (ad + durum/uygunluk
// Tag'leri + Düzenle) → `DetailLayout` (Uygunluk + Kimlik bilgisi Tile'ları solda, Tabs + Tile
// sağda/altta). Sekmeli gövde: **Belgeler** (§13.2) · **Personel** (§13.3/13.5) · **Araçlar** (§23) · **Saha
// Erişimi** (§13.4). Uygunluk özeti belgelerden türetilir; belge değişiminde bu ekran (detail
// query) tazelenir.

import {
  Breadcrumb,
  Button,
  ButtonSet,
  Card,
  CardContent,
  DetailLayout,
  HazardTag,
  PageHeader,
  Spinner,
  TabPanel,
  Tabs,
  Tag,
  cn,
  contractorsApi,
  currentCulture,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { HardHat, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ContractorFormModal } from '../../components/contractors/ContractorFormModal';
import { DocumentsPanel } from '../../components/contractors/DocumentsPanel';
import { PersonnelPanel } from '../../components/contractors/PersonnelPanel';
import { SitesPanel } from '../../components/contractors/SitesPanel';
import { VehiclesPanel } from '../../components/contractors/VehiclesPanel';
import { complianceTone, contractorStatusTone } from '../../components/contractors/tone';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { formatDate } from '../../lib/format';

type DetailTab = 'documents' | 'personnel' | 'vehicles' | 'sites';
const DETAIL_TABS: readonly DetailTab[] = ['documents', 'personnel', 'vehicles', 'sites'];

/** `location.state.openTab` GÜVENİLMEZ (herhangi bir çağıran `navigate(..., { state })`
 * geçebilir) — dört bilinen sekme değeri DIŞINDA her şey (security review, INFO) sessizce
 * `'documents'`e düşer, `useState<DetailTab>`e hiçbir zaman geçersiz bir dize ULAŞMAZ. */
function isDetailTab(value: unknown): value is DetailTab {
  return typeof value === 'string' && (DETAIL_TABS as readonly string[]).includes(value);
}

/** Etiket + değer satırı (kimlik bloğu). Boş değer tire ile gösterilir.
 *
 * Owner review B1 item 8 — 2 sütunlu `grid`in her hücresi CSS'in varsayılan `min-width: auto`
 * kuralı yüzünden İÇERİĞİNİN gerektirdiği genişliğin altına KÜÇÜLEMİYORDU: e-posta gibi boşluksuz
 * uzun bir değer hücreyi zorlayıp komşu "Sözleşme başlangıcı" alanına TAŞIYORDU. `min-w-0` bunu
 * bir grid hücresinde AÇAR (içerik artık kapsayıcıdan geniş olamaz); `break-words` çoğu değer
 * için yeterli, e-posta gibi boşluksuz dizeler `breakAll` ile (yalnız gerekince) `break-all`e
 * geçer. */
function Fact({
  label,
  value,
  className,
  breakAll = false,
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
  /** E-posta gibi boşluksuz, doğal kırılma noktası olmayan değerler için — `break-words` yerine
   * `break-all` (gerektiğinde kelime ORTASINDAN kırar). */
  breakAll?: boolean;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className={cn('text-sm text-fg', breakAll ? 'break-all' : 'break-words')}>
        {value && value.length > 0 ? value : '—'}
      </dd>
    </div>
  );
}

export function ContractorDetailPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const { contractorUuid = '' } = useParams();
  const location = useLocation();

  const [editOpen, setEditOpen] = useState(false);
  // Genel arama bir personel sonucunu açtığında (`HeaderSearch.tsx` — kişi bazlı bir rota YOK,
  // api-contract §34.3) `location.state.openTab` ile Personel sekmesi baştan açık gelir; başka
  // her giriş yolunda (yer imi, F5, sekmeler arası doğrudan tıklama) `state` zaten `null`,
  // `PermitsListPage.tsx`in `backState` desenindeki AYNI "yalnız programatik gezinme taşır" kararı.
  const rawOpenTab = (location.state as { openTab?: unknown } | null)?.openTab;
  const initialTab = isDetailTab(rawOpenTab) ? rawOpenTab : 'documents';
  const [tab, setTab] = useState<DetailTab>(initialTab);
  // Aynı taşeronun başka kişisi için yeni bir gezinme (yeniden mount yok) sekmeyi yine seçer.
  useEffect(() => {
    const next = (location.state as { openTab?: unknown } | null)?.openTab;
    if (isDetailTab(next)) setTab(next);
  }, [location.state]);

  const query = useQuery({
    queryKey: contractorsKeys.detail(contractorUuid),
    queryFn: () => contractorsApi.getContractor(contractorUuid),
    enabled: contractorUuid.length > 0,
  });

  const contractor = query.data;

  const tabItems = [
    { value: 'documents', label: t('contractors.detail.tab.documents') },
    { value: 'personnel', label: t('contractors.detail.tab.personnel') },
    { value: 'vehicles', label: t('contractors.detail.tab.vehicles') },
    { value: 'sites', label: t('contractors.detail.tab.sites') },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          { label: t('contractors.title'), href: '/contractors' },
          ...(contractor ? [{ label: contractor.name }] : []),
        ]}
      />

      {query.isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('contractors.detail.loading')} />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <HardHat className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-sm text-muted">{resolveApiErrorMessage(query.error, t)}</p>
            <Button asChild variant="secondary">
              <Link to="/contractors">{t('contractors.detail.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : contractor ? (
        <>
          <PageHeader
            title={contractor.name}
            id={contractor.legalName ?? undefined}
            tags={
              <>
                {/* Sıra: durum önce, hazard sonra (S34-2 design review §1 "detail header"). */}
                <Tag color={contractorStatusTone(contractor.status)}>
                  {t(`contractors.status.${contractor.status.toLowerCase()}`)}
                </Tag>
                <Tag color={complianceTone(contractor.complianceStatus.overall)}>
                  {t('contractors.detail.header.compliance', {
                    status: t(
                      `contractors.compliance.${contractor.complianceStatus.overall.toLowerCase()}`,
                    ),
                  })}
                </Tag>
                <HazardTag hazardClass={contractor.hazardClass} />
              </>
            }
            actions={
              <ButtonSet>
                <Button variant="ghost" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                  {t('contractors.detail.edit')}
                </Button>
              </ButtonSet>
            }
          />

          <DetailLayout
            meta={
              <>
                <Card>
                  <CardContent className="flex flex-col gap-4">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('contractors.detail.compliance.title')}
                    </h2>
                    <dl className="grid grid-cols-2 gap-4">
                      <Fact
                        label={t('contractors.detail.compliance.documents')}
                        value={String(contractor.complianceStatus.documentCount)}
                      />
                      <Fact
                        label={t('contractors.detail.compliance.expiring')}
                        value={String(contractor.complianceStatus.expiringCount)}
                      />
                      <Fact
                        label={t('contractors.detail.compliance.expired')}
                        value={String(contractor.complianceStatus.expiredCount)}
                      />
                      <Fact
                        label={t('contractors.detail.compliance.overall')}
                        value={t(
                          `contractors.compliance.${contractor.complianceStatus.overall.toLowerCase()}`,
                        )}
                      />
                    </dl>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="flex flex-col gap-4">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('contractors.detail.identity.title')}
                    </h2>
                    <dl className="grid grid-cols-2 gap-4">
                      <Fact
                        label={t('contractors.form.field.taxNumber')}
                        value={contractor.taxNumber}
                      />
                      <Fact
                        label={t('contractors.form.field.taxOffice')}
                        value={contractor.taxOffice}
                      />
                      {/* Hazard artık yalnız başlıkta (yukarıda, outline Tag) — S34-2 design
                          review §1: tek-kaynak, `DepartmentReviewDetailPage.tsx` ile aynı karar. */}
                      <Fact
                        label={t('contractors.form.field.isgKatipNo')}
                        value={contractor.isgKatipNo}
                      />
                      <Fact label={t('contractors.form.field.phone')} value={contractor.phone} />
                      <Fact
                        label={t('contractors.form.field.email')}
                        value={contractor.email}
                        breakAll
                      />
                      <Fact
                        label={t('contractors.form.field.contractStart')}
                        value={
                          contractor.contractStart
                            ? formatDate(contractor.contractStart, culture)
                            : null
                        }
                      />
                      <Fact
                        label={t('contractors.form.field.contractEnd')}
                        value={
                          contractor.contractEnd
                            ? formatDate(contractor.contractEnd, culture)
                            : null
                        }
                      />
                      <Fact
                        className="col-span-2"
                        label={t('contractors.form.field.address')}
                        value={contractor.address}
                      />
                    </dl>
                  </CardContent>
                </Card>
              </>
            }
            content={
              <div className="flex min-w-0 flex-col">
                <Tabs
                  items={tabItems}
                  value={tab}
                  onValueChange={(value) => setTab(value as DetailTab)}
                  ariaLabel={t('contractors.title')}
                  idPrefix="contractor-detail"
                />
                <Card className="carbon:p-0">
                  <CardContent className="carbon:p-0">
                    <TabPanel value={tab} activeValue={tab} idPrefix="contractor-detail">
                      {tab === 'documents' ? (
                        <DocumentsPanel contractorUuid={contractor.uuid} />
                      ) : tab === 'personnel' ? (
                        <PersonnelPanel key={contractor.uuid} contractorUuid={contractor.uuid} />
                      ) : tab === 'vehicles' ? (
                        <VehiclesPanel contractorUuid={contractor.uuid} />
                      ) : (
                        <SitesPanel contractorUuid={contractor.uuid} />
                      )}
                    </TabPanel>
                  </CardContent>
                </Card>
              </div>
            }
          />
        </>
      ) : null}

      {contractor ? (
        <ContractorFormModal open={editOpen} onOpenChange={setEditOpen} contractor={contractor} />
      ) : null}
    </div>
  );
}
